import { ROUTES } from '@/constants';

export interface TenantScopedScreen {
	/**
	 * URL parameters holding ids of the shown tenant (e.g. an open folder),
	 * cleared when the tenant changes.
	 */
	resetParams: readonly string[];
	/**
	 * Another tenant is shown read-only (list screens). Users and IP access
	 * manage it fully: the backend lets platform admins do so.
	 */
	readOnly: boolean;
	/**
	 * The screen shows one record of the tenant (e.g. a building): changing
	 * the tenant leaves it for this list.
	 */
	leaveTo?: string;
}

const LIST: TenantScopedScreen = { resetParams: [], readOnly: true };
const MANAGE: TenantScopedScreen = { resetParams: [], readOnly: false };

/** Screens whose tenant a platform admin picks in the header. */
export const TENANT_SCOPED_SCREENS: Readonly<
	Record<string, TenantScopedScreen>
> = {
	[ROUTES.displays]: LIST,
	[ROUTES.media]: { resetParams: ['folder', 'customer'], readOnly: true },
	[ROUTES.playlists]: LIST,
	[ROUTES.schedules]: LIST,
	[ROUTES.customers]: { resetParams: ['customer'], readOnly: true },
	[ROUTES.audit]: LIST,
	[ROUTES.reports]: LIST,
	[ROUTES.users]: MANAGE,
	// IP access (MFA, on the same page, is the caller's own account).
	[ROUTES.settingsAuthentication]: MANAGE,
	[ROUTES.buildings]: LIST,
	// The tenant's update policy: platform admins manage it for any tenant.
	[ROUTES.settingsAppUpdate]: MANAGE,
};

/** Screens under a path (a record's pages), matched by prefix. */
const TENANT_SCOPED_PREFIXES: ReadonlyArray<{
	prefix: string;
	screen: TenantScopedScreen;
}> = [
	{
		// A building and its floor plans.
		prefix: `${ROUTES.buildings}/`,
		screen: { resetParams: [], readOnly: true, leaveTo: ROUTES.buildings },
	},
];

export function tenantScopedScreen(
	pathname: string,
): TenantScopedScreen | undefined {
	if (Object.hasOwn(TENANT_SCOPED_SCREENS, pathname)) {
		return TENANT_SCOPED_SCREENS[pathname];
	}
	return TENANT_SCOPED_PREFIXES.find(({ prefix }) =>
		pathname.startsWith(prefix),
	)?.screen;
}
